import React from "react";
import {Button, Dropdown, Input} from "gd-react";
import {confirmAlert} from "react-confirm-alert";
import {errorMessage, formatValue, hex} from "./zigbeeDebugShared";

// ZCL type names (from the device's zigbeeClusters.ts) grouped by the input that edits them.
const NUMERIC_TYPE = /^(U?INT\d+|ENUM\d+|BITMAP\d+|DATA\d+|SINGLE_PREC|DOUBLE_PREC|SEMI_PREC|UTC|TOD|DATE|CLUSTER_ID|ATTR_ID|BAC_OID)$/;
const STRING_TYPE = /^(CHAR_STR|LONG_CHAR_STR)$/;

export function inputKind(type) {
	if (type === 'BOOLEAN') return 'boolean';
	if (NUMERIC_TYPE.test(type)) return 'number';
	if (STRING_TYPE.test(type)) return 'text';
	return 'json';
}

// Turns what an input holds into the value herdsman encodes for that ZCL type, or throws with why it cannot.
export function parseZclValue(type, draft, limits) {
	const kind = inputKind(type);
	const text = draft === undefined || draft === null ? '' : String(draft).trim();
	if (kind === 'boolean') {
		if (text !== 'true' && text !== 'false') throw new Error('Choose true or false');
		return text === 'true';
	}
	if (kind === 'number') {
		// Bitmaps and IDs are written in hex as often as decimal.
		const value = /^0x[0-9a-f]+$/i.test(text) ? parseInt(text, 16) : Number(text);
		if (text === '' || !Number.isFinite(value)) throw new Error('Enter a number (decimal or 0x hex)');
		if (limits && limits.min !== undefined && value < limits.min) throw new Error('Must be at least ' + limits.min);
		if (limits && limits.max !== undefined && value > limits.max) throw new Error('Must be at most ' + limits.max);
		return value;
	}
	if (kind === 'text') return String(draft === undefined || draft === null ? '' : draft);
	try {
		return JSON.parse(text);
	} catch (e) {
		throw new Error('Enter JSON, e.g. [1, 2] or {"a": 1}');
	}
}

// Firmware that predates the flag only ever sent vendor entries.
export function isVendor(entry) {
	return !entry || entry.vendor !== false;
}

function ZclTag({entry}) {
	return isVendor(entry) ? null : <span className={'zb-tag'}>ZCL</span>;
}

// Blank means "let the device decide"; anything else must be a code.
export function parseManufacturerCode(draft) {
	const text = String(draft || '').trim();
	if (text === '') return undefined;
	const value = /^0x[0-9a-f]+$/i.test(text) ? parseInt(text, 16) : Number(text);
	if (!Number.isInteger(value) || value < 0 || value > 0xffff) throw new Error('Manufacturer code must be 0-0xffff');
	return value;
}

function ValueInput({type, name, value, onChange}) {
	const kind = inputKind(type);
	if (kind === 'boolean') {
		return <Dropdown name={name} value={value} onChange={onChange}
						 fixeditems={[{title: 'true', value: 'true'}, {title: 'false', value: 'false'}]}/>;
	}
	return <Input name={name} value={value} onChange={onChange}
				  placeholder={kind === 'number' ? '0 or 0x00' : kind === 'json' ? 'JSON' : ''}/>;
}

export class AttributeRow extends React.Component {

	constructor(props) {
		super(props);
		this.state = {draft: ''};
		this.write = this.write.bind(this);
	}

	write() {
		const attr = this.props.attribute;
		let value;
		try {
			value = parseZclValue(attr.type, this.state.draft, attr);
		} catch (err) {
			this.props.onError(attr.name, err.message);
			return Promise.reject(err);
		}
		return this.props.onWrite(attr, value);
	}

	render() {
		const attr = this.props.attribute;
		const result = this.props.result || {};
		const shown = formatValue(result.value);
		return (
			<tr className={isVendor(attr) ? '' : 'zb-standard'}>
				<td>
					<div className={'zb-attr-name'}>{attr.name}<ZclTag entry={attr}/></div>
					<div className={'zb-muted mono'}>{hex(attr.ID)} · {attr.type}
						{attr.manufacturerCode !== undefined ? ' · mfr ' + hex(attr.manufacturerCode) : ''}</div>
				</td>
				<td className={'zb-attr-value'}>
					{shown !== null ? <span className={'mono'}>{shown}</span>
						: result.error ? null
							: attr.readable ? <span className={'zb-muted'}>not read</span> : <span className={'zb-muted'}>write-only</span>}
					{result.error ? <div className={'zb-inline-error'}>{result.error}</div> : null}
				</td>
				<td className={'zb-attr-actions'}>
					{attr.readable ?
						<Button additionalclasses={'sm'} color={'gd-grey'} label={'Read'} progressRes={true}
								onClick={() => this.props.onRead([attr])}/> : null}
					{attr.writable ?
						<div className={'zb-attr-write'}>
							<ValueInput type={attr.type} name={'zb-attr-' + attr.name} value={this.state.draft}
										onChange={(e) => this.setState({draft: e.target.value})}/>
							<Button additionalclasses={'sm'} color={'gd-blue'} label={'Write'} progressRes={true}
									onClick={this.write}/>
						</div> : null}
				</td>
			</tr>
		);
	}
}

export class CommandForm extends React.Component {

	constructor(props) {
		super(props);
		this.state = {open: false, drafts: {}, error: null, response: undefined};
		this.send = this.send.bind(this);
		this.confirmSend = this.confirmSend.bind(this);
	}

	payload() {
		const drafts = this.state.drafts;
		const payload = {};
		(this.props.command.parameters || []).forEach(function (p) {
			try {
				payload[p.name] = parseZclValue(p.type, drafts[p.name]);
			} catch (err) {
				throw new Error(p.name + ': ' + err.message);
			}
		});
		return payload;
	}

	send() {
		const self = this;
		const command = this.props.command;
		let payload;
		try {
			payload = this.payload();
		} catch (err) {
			this.setState({error: err.message});
			return Promise.reject(err);
		}
		this.setState({error: null, response: undefined});
		return this.props.onSend(command, payload)
			.then(function (res) {
				self.setState({response: res && res.response !== undefined ? res.response : null});
			})
			.catch(function (err) {
				self.setState({error: errorMessage(err, 'send ' + command.name)});
				throw err;
			});
	}

	// A command acts on the device, so it goes out only once confirmed; backing out resolves quietly.
	confirmSend() {
		const self = this;
		const command = this.props.command;
		return new Promise(function (resolve, reject) {
			confirmAlert({
				customUI: ({onClose}) => (
					<div className='dialog'>
						<div className={"top-content"}>
							<h2>Send {command.name}?</h2>
							<p>This sends {self.props.clusterName}.{command.name} ({hex(command.ID, 2)}) to the device.</p>
						</div>
						<div className={"bottom-menu"}>
							<Button label={"Cancel"} color={'gd-grey'} onClick={() => {
								onClose();
								resolve();
							}}/>
							<Button color={'gd-red'} label={"Send"} onClick={() => {
								onClose();
								self.send().then(resolve, reject);
							}}/>
						</div>
					</div>
				)
			});
		});
	}

	render() {
		const self = this;
		const command = this.props.command;
		const parameters = command.parameters || [];
		return (
			<div className={'zb-command' + (isVendor(command) ? '' : ' zb-standard')}>
				<div className={'zb-command-head'} role={'button'} onClick={() => this.setState({open: !this.state.open})}>
					<span className={'zb-command-caret'}>{this.state.open ? '▾' : '▸'}</span>
					<span className={'zb-attr-name'}>{command.name}<ZclTag entry={command}/></span>
					<span className={'zb-muted mono'}>{hex(command.ID, 2)}</span>
					<span className={'zb-muted'}>{parameters.length ? parameters.length + ' parameter' + (parameters.length > 1 ? 's' : '') : 'no parameters'}</span>
				</div>
				{this.state.open ?
					<div className={'zb-command-body'}>
						{parameters.map(function (p) {
							return (
								<div key={p.name} className={'zb-param'}>
									<div className={'zb-param-label'}>{p.name} <span className={'zb-muted mono'}>{p.type}</span></div>
									<ValueInput type={p.type} name={'zb-param-' + p.name} value={self.state.drafts[p.name] || ''}
												onChange={(e) => self.setState({drafts: {...self.state.drafts, [p.name]: e.target.value}})}/>
								</div>
							);
						})}
						<Button additionalclasses={'sm'} color={'gd-red'} label={'Send'} progressRes={true} onClick={this.confirmSend}/>
						{this.state.error ? <div className={'zb-note bad'}>{this.state.error}</div> : null}
						{this.state.response !== undefined ?
							<div className={'zb-response'}>
								<div className={'zb-label'}>Response</div>
								{this.state.response === null
									? <span className={'zb-muted'}>No response frame</span>
									: <pre className={'mono'}>{JSON.stringify(this.state.response, null, 2)}</pre>}
							</div> : null}
					</div> : null}
			</div>
		);
	}
}

export class CustomClusterBox extends React.Component {

	constructor(props) {
		super(props);
		const cluster = props.cluster;
		this.state = {
			endpoint: cluster.endpoints && cluster.endpoints.length ? String(cluster.endpoints[0]) : '',
			manufacturerCode: cluster.manufacturerCode !== undefined ? hex(cluster.manufacturerCode) : '',
			// Per attribute: {value, error}.
			results: {},
			// The whole cluster from zigbee.getCluster, fetched the first time "Show standard" is pressed.
			full: null,
			showStandard: false,
			fullError: null,
			showMessages: false,
		};
		this.toggleStandard = this.toggleStandard.bind(this);
		this.read = this.read.bind(this);
		this.readAll = this.readAll.bind(this);
		this.write = this.write.bind(this);
		this.send = this.send.bind(this);
		this.setError = this.setError.bind(this);
	}

	// Endpoint and manufacturer code for one entry: its own code wins, a vendor entry without one takes
	// the box's, and a standard ZCL entry takes none - a vendor code on it gets UNSUPPORTED_ATTRIBUTE back.
	address(entry) {
		const options = {};
		if (this.state.endpoint !== '') options.endpoint = Number(this.state.endpoint);
		const code = entry && entry.manufacturerCode !== undefined
			? entry.manufacturerCode
			: isVendor(entry) ? parseManufacturerCode(this.state.manufacturerCode) : undefined;
		if (code !== undefined) options.manufacturerCode = code;
		return options;
	}

	// What the box shows: the vendor view from getDevice, or the full cluster once it is toggled on.
	cluster() {
		return this.state.showStandard && this.state.full ? this.state.full : this.props.cluster;
	}

	toggleStandard() {
		const self = this;
		if (this.state.showStandard || this.state.full) {
			this.setState({showStandard: !this.state.showStandard});
			return Promise.resolve();
		}
		this.setState({fullError: null});
		return this.props.onRaw('zigbee.getCluster', {cluster: this.props.cluster.name})
			.then(function (full) {
				self.setState({full, showStandard: true});
			})
			.catch(function (err) {
				self.setState({fullError: errorMessage(err, 'load the standard entries')});
				throw err;
			});
	}

	merge(update) {
		this.setState(function (prev) {
			return {results: {...prev.results, ...update}};
		});
	}

	setError(name, message) {
		this.merge({[name]: {...(this.state.results[name] || {}), error: message}});
	}

	// One read per resolved manufacturer code, since herdsman refuses to mix codes in a single frame.
	read(attributes) {
		const self = this;
		const cluster = this.props.cluster;
		const groups = {};
		try {
			attributes.forEach(function (a) {
				const options = self.address(a);
				const key = String(options.manufacturerCode);
				(groups[key] = groups[key] || {options, attributes: []}).attributes.push(a);
			});
		} catch (err) {
			attributes.forEach(a => self.setError(a.name, err.message));
			return Promise.reject(err);
		}
		return Promise.all(Object.keys(groups).map(function (key) {
			const group = groups[key].attributes;
			const options = groups[key].options;
			return self.props.onRaw('zigbee.read', {cluster: cluster.name, attributes: group.map(a => a.name), ...options})
				.then(function (values) {
					const update = {};
					group.forEach(function (a) {
						update[a.name] = values && values[a.name] !== undefined
							? {value: values[a.name], error: null}
							: {value: undefined, error: 'The device did not return it'};
					});
					self.merge(update);
				})
				.catch(function (err) {
					const update = {};
					group.forEach(a => { update[a.name] = {value: undefined, error: errorMessage(err, 'read ' + a.name)}; });
					self.merge(update);
					throw err;
				});
		}));
	}

	readAll() {
		return this.read(this.cluster().attributes.filter(a => a.readable));
	}

	write(attribute, value) {
		const self = this;
		let options;
		try {
			options = this.address(attribute);
		} catch (err) {
			this.setError(attribute.name, err.message);
			return Promise.reject(err);
		}
		return this.props.onRaw('zigbee.write', {cluster: this.props.cluster.name, attributes: {[attribute.name]: value}, ...options})
			.then(function () {
				// Written, not read back: the value shown is the one sent until a Read confirms it.
				self.merge({[attribute.name]: {value, error: null}});
			})
			.catch(function (err) {
				self.setError(attribute.name, errorMessage(err, 'write ' + attribute.name));
				throw err;
			});
	}

	send(command, payload) {
		let options;
		try {
			options = this.address(command);
		} catch (err) {
			return Promise.reject(err);
		}
		return this.props.onRaw('zigbee.command', {cluster: this.props.cluster.name, command: command.name, payload, ...options});
	}

	render() {
		const self = this;
		const cluster = this.cluster();
		const readable = cluster.attributes.filter(a => a.readable);
		const endpoints = cluster.endpoints || [];
		const messages = cluster.responses || [];
		const standardCount = this.state.full
			? this.state.full.attributes.length + this.state.full.commands.length
			- this.props.cluster.attributes.length - this.props.cluster.commands.length
			: null;
		return (
			<div className={'zb-cluster'}>
				<div className={'zb-cluster-head'}>
					<div>
						<span className={'zb-endpoint-title'}>{cluster.name}</span>
						<span className={'zb-muted mono'}> {hex(cluster.ID)}</span>
					</div>
					<div className={'zb-cluster-badges'}>
						{/* Only an extension has standard entries to reveal; a vendor-only cluster is shown whole already. */}
						{cluster.extendsStandard ?
							<Button additionalclasses={'sm'} color={'gd-grey'} progressRes={true} onClick={this.toggleStandard}
									label={this.state.showStandard ? 'Hide standard'
										: 'Show standard' + (standardCount !== null ? ' (' + standardCount + ')' : '')}/> : null}
						{cluster.extendsStandard ? <span className={'zb-pill neutral'}>vendor additions</span>
							: <span className={'zb-pill warn'}>vendor cluster</span>}
					</div>
				</div>

				{this.state.fullError ? <div className={'zb-note bad'}>{this.state.fullError}</div> : null}

				<div className={'zb-cluster-address'}>
					{endpoints.length > 1 ?
						<label>Endpoint
							<Dropdown name={'zb-ep-' + cluster.name} value={this.state.endpoint}
									  onChange={(e) => this.setState({endpoint: e.target.value})}
									  fixeditems={endpoints.map(ep => ({title: String(ep), value: String(ep)}))}/>
						</label>
						: <label>Endpoint
							<span className={'zb-address-value'}>{endpoints.length === 1 ? endpoints[0] : 'device default'}</span>
						</label>}
					<label>Manufacturer code
						<Input name={'zb-mfr-' + cluster.name} value={this.state.manufacturerCode} placeholder={'none'}
							   onChange={(e) => this.setState({manufacturerCode: e.target.value})}/>
					</label>
				</div>

				{cluster.attributes.length ?
					<div>
						<div className={'zb-cluster-section'}>
							<span className={'zb-section-label'}>Attributes ({cluster.attributes.length})</span>
							{readable.length > 1 ?
								<Button additionalclasses={'sm'} color={'gd-grey'} label={'Read all'} progressRes={true}
										onClick={this.readAll}/> : null}
						</div>
						<table className={'zb-table zb-attr-table'}>
							<thead><tr><th>Attribute</th><th>Value</th><th/></tr></thead>
							<tbody>
								{cluster.attributes.map(a => <AttributeRow key={a.name} attribute={a} result={self.state.results[a.name]}
																		   onRead={self.read} onWrite={self.write} onError={self.setError}/>)}
							</tbody>
						</table>
					</div> : null}

				{cluster.commands.length ?
					<div>
						<div className={'zb-cluster-section'}>
							<span className={'zb-section-label'}>Commands ({cluster.commands.length})</span>
						</div>
						{cluster.commands.map(c => <CommandForm key={c.name} command={c} clusterName={cluster.name} onSend={self.send}/>)}
					</div> : null}

				{/* What the device sends the hub, e.g. a command's reply; for reference, the hub cannot send these. */}
				{messages.length ?
					<div>
						<div className={'zb-cluster-section zb-toggle'} role={'button'}
							 onClick={() => this.setState({showMessages: !this.state.showMessages})}>
							<span className={'zb-section-label'}>
								<span className={'zb-command-caret'}>{this.state.showMessages ? '▾' : '▸'}</span>
								Messages from the device ({messages.length})
							</span>
						</div>
						{this.state.showMessages ?
							<table className={'zb-table'}>
								<thead><tr><th>Message</th><th>Parameters</th></tr></thead>
								<tbody>
									{messages.map(m => (
										<tr key={m.name} className={isVendor(m) ? '' : 'zb-standard'}>
											<td><span className={'zb-attr-name'}>{m.name}<ZclTag entry={m}/></span>
												<div className={'zb-muted mono'}>{hex(m.ID, 2)}</div></td>
											<td className={'mono'}>{m.parameters.length
												? m.parameters.map(p => p.name + ': ' + p.type).join(', ')
												: <span className={'zb-muted'}>none</span>}</td>
										</tr>
									))}
								</tbody>
							</table> : null}
					</div> : null}
			</div>
		);
	}
}

// Absent from firmware that predates zigbee.write/zigbee.command, and empty for devices whose definition adds nothing.
export default function ZigbeeCustomClusters({clusters, onRaw}) {
	if (!clusters || clusters.length === 0) return null;
	return (
		<div className={'zb-clusters'}>
			<div className={'zb-heading'}>Custom clusters <span className={'zb-muted'}>({clusters.length})</span></div>
			<p className={'zb-clusters-intro'}>Vendor clusters and entries on this device; Show standard adds the ZCL entries they sit
				beside. Reads, writes and commands go straight to the device as raw ZCL, bypassing the converters.</p>
			{clusters.map(c => <CustomClusterBox key={c.name} cluster={c} onRaw={onRaw}/>)}
		</div>
	);
}
